import React from 'react';
import ApiProvider from '../../../core/api/ApiProvider';
import * as Yup from 'yup';
import { Form as FormikForm, Formik } from 'formik';
import { Alert, AreaField, Button, Form, Loader } from '../../../core/components';
import { InfoMessageService, TranslationService } from '../../../core/services';
import { DictionaryType } from '../../../core/types/DictionaryType';
import { ResponseApiType } from '../../types/ResponseApiType';
import { CaseDisputeResponseType } from './types/CaseDisputeResponseType';
import { useMutation, useQuery } from 'react-query';
import { ActionStatusType } from '../../../core/types/ActionStatusType';
import { useParams } from 'react-router-dom';

type FormValuesType = {
  comment: string;
};

type Props = {
  onClose: (isOpenInstallmentModal: boolean) => void;
};

const validationSchema = Yup.object().shape({
  comment: Yup.string().required(TranslationService.translate('RequiredField')),
});

const CaseDispute = (props: Props) => {
  const { caseNumber, tenantUId } = useParams() as { caseNumber: string; tenantUId: string };

  const caseDisputeSettings = useQuery<ActionStatusType<CaseDisputeResponseType>>(
    ['caseDisputeSettings', caseNumber],
    () =>
      ApiProvider.get<ResponseApiType<CaseDisputeResponseType>>(
        `CaseDisputeModule/GetCaseDisputeSettings/${caseNumber}`
      ),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      onSuccess: (data) => {
        if (data.success) {
        } else {
          InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
        }
      },
      onError: () => {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      },
    }
  );

  const caseDispute = useMutation(
    (values: DictionaryType<string>) => {
      return ApiProvider.post<ResponseApiType>(`CaseDisputeModule/${values.path}`, {
        caseNo: caseNumber,
        comment: values.comment,
        sourceTenantId: tenantUId,
      });
    },
    {
      onSuccess: (data) => {
        props.onClose(false);

        if (data.success) {
          InfoMessageService.success(
            TranslationService.translate('CaseDisputeRequestSendSuccessfully')
          );
        } else {
          InfoMessageService.error(data.viewMessage);
        }
      },
      onError: () => {
        InfoMessageService.error(TranslationService.translate('ErrorSthWentWrong'));
      },
    }
  );

  const handleSendForm = (values: FormValuesType) => {
    if (caseDisputeSettings && caseDisputeSettings.data) {
      caseDispute.mutate({
        comment: values.comment,
        caseNumber: caseNumber,
        sourceTenantId: tenantUId,
        path: 'RequestCaseDispute',
      });
    }
  };

  return (
    <>
      {caseDisputeSettings.isLoading || caseDisputeSettings.isFetching ? (
        <Loader />
      ) : (
        caseDisputeSettings &&
        caseDisputeSettings.data?.success &&
        (caseDisputeSettings.data.data.existingCaseDispute ? (
          <Alert variant={Alert.Variants.Info} className="mb-2">
            {TranslationService.translate('CaseDisputeAlreadyRequested')}
            <br />
          </Alert>
        ) : (
          <>
            <Formik
              initialValues={{
                comment: '',
              }}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                handleSendForm(values);
              }}
            >
              {({ values, handleChange, isValid, errors }) => (
                <FormikForm>
                  <Form.Group className="mb-2">
                    <Form.Label>
                      <span
                        style={{
                          whiteSpace: 'break-spaces',
                          lineHeight: 1.3,
                        }}
                      >
                        {TranslationService.translate('Comment')}
                      </span>
                    </Form.Label>

                    <AreaField
                      name="comment"
                      id="casedispute-commentfield"
                      cols={30}
                      rows={4}
                      errors={errors.comment}
                      className="form-control textarea-lg"
                      value={values.comment}
                      ariaLabel={TranslationService.translate('CaseDispute')}
                      onChange={(value, e) => {
                        handleChange(e);
                      }}
                    />
                  </Form.Group>

                  <Button
                    id="SendCaseDispute"
                    type={Button.Types.Submit}
                    variant={Button.Variants.Primary}
                    disabled={!isValid || caseDispute.isLoading}
                    inProgress={caseDispute.isLoading}
                  >
                    {TranslationService.translate('Send')}
                  </Button>
                </FormikForm>
              )}
            </Formik>
          </>
        ))
      )}
    </>
  );
};

export default CaseDispute;